"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { DEFAULT_LANGUAGE, resolveI18nText } from "@/sanity/globals/I18n";
import { IPricingSectionV2 } from "@/sanity/lib/definitions";
import { IPricingV2Discount } from "@/sanity/templates";
import { GroqImage } from "@/helpers/Image";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import { useCarouselApi } from "@/hooks/useCarouselApi";
import { Dialog, DialogContent, DialogTrigger } from "@/shadcn/ui/dialog";
import Link from "next/link";
import AssetTabs from "./AssetTabs";
import CategorySelector from "./CategorySelector";
import TierCard from "./TierCard";
import { findAsset, findCategory, resolveNewBadgeLabel } from "./helpers";
import { ArrowUpRightIcon, PlatformPopupContent } from "./PricingPlatformsBlock";
import { IPlatformItem } from "@/sanity/lib/definitions";
import { resolveLink } from "@/sanity/globals/CTA";
import { IPricingTier } from "@/sanity/lib/definitions";
import type { QuizPlanSelection } from "./quiz/PlanQuizDialog";
import { resolveQuizCopy } from "./quiz/quizCopy";

const PlanQuizDialog = dynamic(() => import("./quiz/PlanQuizDialog"), { ssr: false });

const ease = [0.25, 0.1, 0.25, 1] as const;

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function HelpCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M9.6 9.2a2.4 2.4 0 1 1 3.32 2.21c-.68.29-1.17.83-1.17 1.57v.52"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle
        cx="11.75"
        cy="16.6"
        r="1.1"
        fill="currentColor"
      />
    </svg>
  );
}

export type TiersFilter = {
  /** Force a specific asset — hides asset tabs. */
  assetKey?: string;
  /** Force a specific category — hides the category selector. */
  categoryKey?: string;
  /** Force a single tier — hides everything except that one card. */
  tierKey?: string;
};

export default function PricingTiersBlock({
  data,
  language,
  filter,
  discount,
}: {
  data: IPricingSectionV2;
  language?: string;
  filter?: TiersFilter;
  discount?: IPricingV2Discount;
}) {
  const assets = data.assets ?? [];

  const initialAssetKey = filter?.assetKey ?? assets[0]?.key ?? "";
  const [activeAssetKey, setActiveAssetKey] = useState<string>(initialAssetKey);

  useEffect(() => {
    if (filter?.assetKey && filter.assetKey !== activeAssetKey) {
      setActiveAssetKey(filter.assetKey);
    }
  }, [filter?.assetKey, activeAssetKey]);

  const activeAsset = findAsset(data, activeAssetKey);
  const categories = activeAsset?.categories ?? [];

  const initialCategoryKey = filter?.categoryKey ?? categories[0]?.key ?? "";
  const [activeCategoryKey, setActiveCategoryKey] = useState<string>(initialCategoryKey);
  const [isFunded, setIsFunded] = useState<boolean>(false);

  const effectiveCategoryKey = useMemo(() => {
    if (filter?.categoryKey) return filter.categoryKey;
    if (categories.some((c) => c.key === activeCategoryKey)) return activeCategoryKey;
    return categories[0]?.key ?? "";
  }, [filter?.categoryKey, activeCategoryKey, categories]);

  const activeCategory = findCategory(activeAsset, effectiveCategoryKey);

  const visibleTiers = useMemo(() => {
    const tiers = activeCategory?.tiers ?? [];
    if (filter?.tierKey) return tiers.filter((t) => t.key === filter.tierKey);
    return tiers;
  }, [activeCategory?.tiers, filter?.tierKey]);

  const logoGroup = useMemo(() => {
    if (!activeAsset?.platformLogosGroupKey) return undefined;
    return data.platformsFooter?.groups?.find((g) => g.key === activeAsset.platformLogosGroupKey);
  }, [activeAsset, data.platformsFooter]);

  const showAssetTabs = !filter?.assetKey && assets.length > 1;
  const showCategorySelector = !filter?.categoryKey && !filter?.tierKey && categories.length > 0;
  const showLogos = !filter?.tierKey && logoGroup?.items?.length;

  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } = useCarouselApi();
  const showMobileCarousel = visibleTiers.length > 1;

  /* ── "Help me choose" quiz ── */
  const prefersReducedMotion = useReducedMotion();
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizLoaded, setQuizLoaded] = useState(false);
  const [highlightPlanKey, setHighlightPlanKey] = useState<string | null>(null);
  const quizCopy = resolveQuizCopy(language);
  const showQuizEntry = !filter?.categoryKey && !filter?.tierKey;

  // Floating entry button: visible while the pricing block is on screen,
  // expanded (icon + label) for a few seconds after appearing and on
  // hover/focus, collapsed to an icon-only circle otherwise.
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [fabInView, setFabInView] = useState(false);
  const [fabPeek, setFabPeek] = useState(false);
  const [fabHover, setFabHover] = useState(false);
  const fabPeekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showQuizEntry) return;
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setFabInView(entry.isIntersecting), {
      rootMargin: "-10% 0px -10% 0px",
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [showQuizEntry]);

  useEffect(() => {
    if (fabInView) {
      setFabPeek(true);
      if (fabPeekTimer.current) clearTimeout(fabPeekTimer.current);
      fabPeekTimer.current = setTimeout(() => setFabPeek(false), 2000);
    } else {
      setFabPeek(false);
      if (fabPeekTimer.current) clearTimeout(fabPeekTimer.current);
    }
    return () => {
      if (fabPeekTimer.current) clearTimeout(fabPeekTimer.current);
    };
  }, [fabInView]);

  const fabExpanded = fabPeek || fabHover;

  const openQuiz = () => {
    setQuizLoaded(true);
    setQuizOpen(true);
  };

  // Optional deep link: ?quiz=open opens the quiz on load (additive).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("quiz") === "open") {
      setQuizLoaded(true);
      setQuizOpen(true);
    }
  }, []);

  const handleQuizSeeDetails = (selection: QuizPlanSelection) => {
    setQuizOpen(false);
    setActiveAssetKey(selection.asset);
    setActiveCategoryKey(selection.product);
    setIsFunded(false);
    const key = `${selection.asset}:${selection.product}:${selection.tierKey}`;
    setHighlightPlanKey(key);
    // Wait for the dialog to close and the tab/category switch to render.
    window.setTimeout(() => {
      const targets = Array.from(document.querySelectorAll<HTMLElement>(`[data-quiz-plan="${key}"]`));
      const visible = targets.find((el) => el.offsetParent !== null) ?? targets[0];
      visible?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
      });
    }, 350);
    window.setTimeout(() => setHighlightPlanKey(null), 2550);
  };

  const planKeyFor = (tierKey?: string) => `${activeAssetKey}:${effectiveCategoryKey}:${tierKey ?? ""}`;

  const renderTierCard = (tier: IPricingTier, idx: number) => {
    if (!activeCategory) return null;
    return (
      <div
        data-quiz-plan={planKeyFor(tier.key)}
        className="relative h-full rounded-[.125rem]">
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 z-10 rounded-[.125rem] ring-[3px] ring-inset ring-[#35c299] shadow-[inset_0_0_4rem_rgba(53,194,153,0.35)] transition-opacity duration-500",
            highlightPlanKey === planKeyFor(tier.key) ? "opacity-100" : "opacity-0",
          )}
        />
        <TierCard
          tier={tier}
          category={activeCategory}
          assetKey={activeAssetKey}
          specsCatalog={data.specsCatalog}
          language={language}
          index={idx}
          isFunded={isFunded}
          onFlip={() => setIsFunded((v) => !v)}
          discount={discount}
          sharedFlipToFundedLabel={data.flipToFundedLabel}
          sharedFlipToFundedLabelI18n={data.flipToFundedLabelI18n}
          sharedFlipBackLabel={data.flipBackLabel}
          sharedFlipBackLabelI18n={data.flipBackLabelI18n}
        />
      </div>
    );
  };

  return (
    <div
      ref={sectionRef}
      className="flex w-full flex-col items-center gap-[3rem] md:gap-[4rem]">
      {showAssetTabs && (
        <AssetTabs
          assets={assets}
          activeKey={activeAssetKey}
          onSelect={(key) => {
            setActiveAssetKey(key);
            const nextAsset = assets.find((a) => a.key === key);
            const nextCategoryKey = nextAsset?.categories?.[0]?.key ?? "";
            setActiveCategoryKey(nextCategoryKey);
            setIsFunded(false);
          }}
          language={language}
        />
      )}

      {showLogos ? (
        <div className="flex flex-wrap items-center justify-center gap-[1rem] md:gap-[1.5rem]">
          {logoGroup!.items!.map((item) => (
            <TopPlatformBadge
              key={item._key ?? item.name}
              item={item}
              language={language}
            />
          ))}
        </div>
      ) : null}

      {showCategorySelector && (
        <CategorySelector
          categories={categories}
          activeKey={effectiveCategoryKey}
          onSelect={(key) => {
            setActiveCategoryKey(key);
            setIsFunded(false);
          }}
          language={language}
        />
      )}

      {activeCategory && visibleTiers.length > 0 ? (
        <>
          {/* Mobile carousel */}
          {showMobileCarousel && (
            <div className="flex w-full flex-col gap-[3rem] md:hidden">
              <Carousel
                setApi={setApi}
                opts={{ align: "center", loop: false }}
                className="w-full">
                <CarouselContent className="gap-[1.5rem]">
                  {visibleTiers.map((tier, idx) => (
                    <CarouselItem
                      key={tier._key ?? tier.key}
                      className="basis-full pl-0">
                      {renderTierCard(tier, idx)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {count > 1 && (
                <div className="mx-auto flex w-[min(50rem,100%)] items-center gap-[2rem]">
                  <button
                    type="button"
                    onClick={handlePrev}
                    disabled={isFirst}
                    aria-label="Previous"
                    className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black/10 transition-opacity hover:opacity-70 disabled:opacity-30">
                    <ArrowIcon className="h-[2.125rem] w-[2.125rem] rotate-180 text-black" />
                  </button>
                  <div className="h-[0.5rem] flex-1 overflow-hidden rounded-[3rem] bg-black/10">
                    <motion.div
                      className="h-full rounded-full bg-black"
                      animate={{
                        width: `${count > 1 ? ((current - 1) / (count - 1)) * 100 : 100}%`,
                      }}
                      transition={{ duration: 0.3, ease }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={isLast}
                    aria-label="Next"
                    className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black transition-opacity hover:opacity-80 disabled:opacity-30">
                    <ArrowIcon className="h-[2.125rem] w-[2.125rem] text-white" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Desktop grid (also handles single-tier layout on all screens) */}
          <div
            className={cn(
              "grid w-full gap-[1.5rem]",
              showMobileCarousel && "hidden md:grid",
              visibleTiers.length === 1 && "grid-cols-1",
              visibleTiers.length === 2 && "md:grid-cols-2",
              visibleTiers.length >= 3 && "md:grid-cols-2 lg:grid-cols-3",
            )}>
            {visibleTiers.map((tier, idx) => (
              <div
                key={tier._key ?? tier.key}
                className="h-full">
                {renderTierCard(tier, idx)}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {showQuizEntry && (
        <AnimatePresence>
          {fabInView && !quizOpen && (
            <motion.button
              type="button"
              onClick={openQuiz}
              onMouseEnter={() => setFabHover(true)}
              onMouseLeave={() => setFabHover(false)}
              onFocus={() => setFabHover(true)}
              onBlur={() => setFabHover(false)}
              aria-label={quizCopy.entryLabel}
              initial={{ opacity: 0, y: "calc(-50% + 16px)" }}
              animate={{ opacity: 1, y: "-50%" }}
              exit={{ opacity: 0, y: "calc(-50% + 16px)" }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.25, ease }}
              className="fixed top-[88%] right-[2.5rem] z-[90] flex h-[48px] min-w-[48px] items-center justify-center gap-[1.25rem] overflow-hidden rounded-full bg-[#1aa87f] px-[10px] text-white shadow-[0_12px_30px_rgba(0,27,20,0.35)] transition-colors hover:bg-[#12805f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1aa87f] focus-visible:ring-offset-2 lg:top-[85%]">
              <HelpCircleIcon className="h-[28px] w-[28px] shrink-0" />
              <AnimatePresence initial={false}>
                {fabExpanded && (
                  <motion.span
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "auto", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.25, ease }}
                    className="overflow-hidden whitespace-nowrap">
                    <span className="block pr-[10px] font-geist text-[18px] leading-none">{quizCopy.entryLabel}</span>
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          )}
        </AnimatePresence>
      )}

      {quizLoaded && (
        <PlanQuizDialog
          open={quizOpen}
          onOpenChange={setQuizOpen}
          data={data}
          language={language}
          discount={discount}
          activeAssetKey={activeAssetKey}
          onSeeDetails={handleQuizSeeDetails}
        />
      )}
    </div>
  );
}

function TopPlatformBadge({ item, language }: { item: IPlatformItem; language?: string }) {
  const name = resolveI18nText(item.name, item.nameI18n, language);
  const hasPopup = !!item.popupBullets?.length || !!item.popupImage;
  // Without an href the badge stays inert (the `: badge` branch below) rather
  // than opening the homepage in a new tab.
  const href = item.href?.trim();
  const link = href ? resolveLink(href, language || DEFAULT_LANGUAGE) : "";
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  const badge = (
    <div className="group inline-flex items-center gap-[1rem] rounded-[0.375rem] border border-black/10 bg-white px-[1.75rem] py-[1.25rem] transition-colors hover:bg-[#f4f4f4] md:px-[2.25rem] md:py-[1.5rem]">
      {item.logo && (
        <div className="relative flex h-[2.75rem] w-auto shrink-0 items-center md:h-[3.25rem]">
          <GroqImage
            src={item.logo}
            alt={name ?? ""}
            className="!h-full !w-auto object-contain"
          />
        </div>
      )}
      {item.isNew && (
        <span className="inline-flex items-center rounded-full bg-[#3d8c74] px-[0.75rem] py-[0.25rem] font-geist text-[1.25rem] leading-[1.2] font-medium text-white uppercase">
          {resolveNewBadgeLabel(language)}
        </span>
      )}
      <span
        className="shrink-0 text-black/60 transition-colors group-hover:text-black"
        aria-hidden="true">
        <ArrowUpRightIcon className="h-[1.5rem] w-[1.5rem] md:h-[1.75rem] md:w-[1.75rem]" />
      </span>
    </div>
  );

  if (hasPopup) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            className="cursor-pointer">
            {badge}
          </button>
        </DialogTrigger>
        <DialogContent className="!w-[min(120rem,calc(100%-4rem))] rounded-[0.75rem] bg-white p-[2rem] pb-[3.75rem]">
          <PlatformPopupContent
            item={item}
            name={name ?? ""}
            language={language}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return finalLink ? (
    <Link
      href={finalLink}
      target="_blank"
      rel="noopener noreferrer">
      {badge}
    </Link>
  ) : (
    badge
  );
}
